import { useState } from "react";
import useModuleTemplates from "../hooks/queries/useModuleTemplates";
import { GridLegacy as Grid, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Tooltip, Typography, TextField, InputAdornment, Switch, FormControlLabel } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import EnhancedTableHead from "../../components/table/EnhancedTableHead";
import moduleAreas from "../constants/moduleAreaConstants";
import CardBase from "../../components/cards/CardBase";
import { Archive, Edit, MoreVert, History, ControlPointDuplicate, Clear as ClearIcon } from "@mui/icons-material";
import ArchiveTemplateDialog from "./dialogs/ArchiveTemplateDialog";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import TemplateNameCell from "./TemplateNameCell";
import { Link, useRouteMatch } from "@/compat/router";
import { useAuth } from "../../contexts/authContext";
import TemplateVersionHistoryDialog from "./riskassessments/dialogs/TemplateVersionHistoryDialog";
import { setTablePage } from "../../redux/actions/tablePageActions";
import { tablePage } from "../constants/tablePageConstants";
import { sortOrderName } from "../constants/sortOrder";
import ResponsiveSelect from "../../components/ui/ResponsiveSelect";
import { frequencyDropdownValues } from "../constants/frequencyConstants";
import useSubjects from "../hooks/queries/useSubjects";
import useSectors from "../hooks/queries/useSectors";

const useStyles = makeStyles((theme) => ({
    menuItem: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: "flex",
        gap: theme.spacing(1),
        fontSize: "0.875rem",
    },
    searchTextField: {
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
        width: "100%",
        '& .clearIcon': { visibility: 'hidden' },
        '&:hover .clearIcon': { visibility: 'visible' },
    },
    endAdornment: { paddingRight: theme.spacing(1) },
    tooltip: {
        margin: theme.spacing(1),
        backgroundColor: 'white',
        border: '1px solid #000',
        color: theme.palette.common.black,
        fontSize: '0.9em',
        borderRadius: 0,
        padding: theme.spacing(1),
    },
    iconBtn: {},
    clearIcon: {},
}));

const defaultDropdownOption = { value: -1, label: "All" };

const headCells = [
    {
        id: "name",
        label: "Name",
        isSortable: true,
    },
    {
        id: "sector",
        label: "Sector",
        isSortable: true,
    },
    {
        id: "subject",
        label: "Subject",
        isSortable: true,
    },
    {
        id: "site",
        label: "Site",
        isSortable: false,
    },
    {
        id: "frequency",
        label: "Frequency",
        isSortable: true,
    },
    {
        id: "actions",
        label: "Actions",
    },
];

const tableKey = tablePage.COMPLIANCE_ADMIN_TEMPLATES_TABLE;

function ComplianceAdminTemplatesTableCard() {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const [archiveTemplate, setArchiveTemplate] = useState(null);

    const sectors = useSectors();
    const subjects = useSubjects();

    const [templateType, setTemplateType] = useState(-1);
    const [sector, setSector] = useState(-1);
    const [subject, setSubject] = useState(-1);
    const [frequency, setFrequency] = useState(-1);
    const [filterTemplateNameText, setFilterTemplateNameText] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    const { selectedSite, activeAppId, appSiteIds, selectedAccount, sites } =
        useAppSelector((state) => state.account);

    const { complianceAdminTemplatesTable } = useAppSelector(
        (state) => state.tablePage
    );

    const currentAccountId = selectedAccount?.account?.externalId;

    const siteExternalIds = selectedSite
        ? [selectedSite.externalId]
        : appSiteIds[activeAppId];

    const match = useRouteMatch();
    const { path } = match;
    const { isWorknestUser } = useAuth();

    const hasTopLevel = appSiteIds[activeAppId]?.includes(currentAccountId);
    const hideOptionForClientUserIfTemplateIsGlobal = (siteExternalId, isWorkNestUser) => (!siteExternalId && (isWorkNestUser || hasTopLevel)) || siteExternalId;

    const templates = useModuleTemplates({
        moduleArea: moduleAreas.COMPLIANCE,
        pageNum: complianceAdminTemplatesTable.page,
        rowsPerPage: complianceAdminTemplatesTable.rowsPerPage,
        accountId: currentAccountId,
        siteExternalIds,
        orderByColumn: complianceAdminTemplatesTable.sortBy || headCells[0].id,
        sortOrder: complianceAdminTemplatesTable.orderBy || sortOrderName.ASC,
        frequency: frequency === -1 ? null : frequency,
        sectorId: sector === -1 ? null : sector,
        subjectId: subject === -1 ? null : subject,
        templateType: templateType === 2 ? true : templateType === 1 ? false : null,
        filterText: filterTemplateNameText && filterTemplateNameText.length >= 4 ? filterTemplateNameText : null,
        isEnabled: !showArchived,
    });

    const handleTemplateTypeChange = (e) => {
        setTemplateType(e.target.value);
    };

    const handleSectorChange = (e) => {
        setSector(e.target.value);
    };

    const handleSubjectChange = (e) => {
        setSubject(e.target.value);
    };

    const handleFrequencyChange = (e) => {
        setFrequency(e.target.value);
    };

    // Live search
    const handleNameSearchChange = (e) => {
        setFilterTemplateNameText(e.target.value);
    };

    const handleNameFilteredTextClear = () => {
        setFilterTemplateNameText("");
    };

    const showFilteredTextError = () => {
        return filterTemplateNameText && filterTemplateNameText.length > 0 && filterTemplateNameText.length < 4;
    };

    const handleRowsPerPageChange = (e) => {
        dispatch(
            setTablePage({
                key: tableKey,
                page: 0,
                rowsPerPage: parseInt(e.target.value),
            })
        );
    };

    const handlePageChange = (e, newPage) => {
        dispatch(
            setTablePage({
                key: tableKey,
                page: newPage,
                rowsPerPage: complianceAdminTemplatesTable.rowsPerPage,
            })
        );
    };

    const handleSortClick = (event, sortColumn) => {
        const isAsc = complianceAdminTemplatesTable.sortBy === sortColumn && complianceAdminTemplatesTable.orderBy === sortOrderName.ASC;
        dispatch(
            setTablePage({
                ...complianceAdminTemplatesTable,
                key: tableKey,
                page: 0,
                sortBy: sortColumn,
                orderBy: isAsc ? sortOrderName.DESC : sortOrderName.ASC,
            })
        );
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setOpen] = useState<number | false>(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState();
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);

    function handleCloseDialog() {
        setVersionDialogOpen(false);
    }

    function handleOpenVersionDialog(id) {
        setSelectedTemplateId(id);
        setVersionDialogOpen(true);
        setOpen(false);
    }

    const handleClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setOpen(id);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setOpen(false);
    };

    const handleShowArchivedChange = (e) => setShowArchived(e.target.checked);

    const tableContent = () => {
        if (templates.data?.results?.length > 0) {
            return templates.data.results.map((t) => (
                <TableRow key={t.id}>
                    <TableCell><TemplateNameCell name={t.name} isWorkNest={!t.siteExternalId} /></TableCell>
                    <TableCell>{t.sector}</TableCell>
                    <TableCell>{t.subject}</TableCell>
                    <TableCell>{sites[t?.siteExternalId]?.name ?? "All"}</TableCell>
                    <TableCell>{t.frequencyDescription}</TableCell>
                    <TableCell>
                        <Tooltip title="More">
                            <IconButton
                                aria-label="more"
                                aria-controls="long-menu"
                                aria-haspopup="true"
                                onClick={(e) => handleClick(e, t.id)}
                            >
                                <MoreVert />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            id="long-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={open === t.id}
                            onClose={handleClose}
                        >
                            <MenuItem
                                className={classes.menuItem}
                                component={Link}
                                to={`${path}/templatebuilder/edit/${t.templateVersionId}`}
                            >
                                <Edit />
                                {isWorknestUser || t.siteExternalId ? "Edit Template" : "Duplicate and Edit Template"}
                            </MenuItem>
                            {t.siteExternalId && (
                                <MenuItem
                                    className={classes.menuItem}
                                    component={Link}
                                    to={`${path}/templatebuilder/edit/${t.templateVersionId}/true`}
                                >
                                    <ControlPointDuplicate />
                                    Duplicate and Edit Custom Template
                                </MenuItem>
                            )}
                            {hideOptionForClientUserIfTemplateIsGlobal(t.siteExternalId, isWorknestUser) &&
                                <MenuItem
                                    className={classes.menuItem}
                                    onClick={() => { setOpen(false); setArchiveTemplate(t) }}>
                                    <Archive />
                                    {t.isEnabled ? 'Archive' : 'Restore'} Template
                                </MenuItem>
                            }
                            <MenuItem
                                className={classes.menuItem}
                                onClick={() => handleOpenVersionDialog(t.id)}
                            >
                                <History />
                                Version History
                            </MenuItem>
                        </Menu>
                    </TableCell>
                </TableRow>
            ));
        }

        return (
            <TableRow>
                <TableCell colSpan={headCells.length}>
                    <Typography>No templates have been added.</Typography>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <>
            <TemplateVersionHistoryDialog
                onClose={handleCloseDialog}
                open={versionDialogOpen}
                templateId={selectedTemplateId}
            />
            <CardBase
                title="Compliance Templates"
                description="A list of all available compliance templates."
                isLoading={templates.isLoading}
                isFetching={templates.isFetching}
                error={templates.error}
            >
                <Grid container spacing={2}>
                    {!isWorknestUser &&
                        <Grid item xs={12} md={6} xl={3}>
                            <ResponsiveSelect
                                options={[
                                    { value: -1, label: "All" },
                                    { value: 1, label: "WorkNest" },
                                    { value: 2, label: "Client" },
                                ]}
                                optionLabelFunction={(option) => option.label}
                                optionValueKey={'value'}
                                margin='dense'
                                fullWidth
                                label="Template Type"
                                placeholder="Template Type"
                                value={templateType}
                                onChange={handleTemplateTypeChange}
                            />
                        </Grid>
                    }
                    <Grid item xs={12} md={6} xl={3}>
                        <ResponsiveSelect
                            options={[defaultDropdownOption].concat(sectors.data?.map((s) => ({
                                value: s.id,
                                label: s.name,
                            })))}
                            optionLabelFunction={(option) => option.label}
                            optionValueKey={'value'}
                            margin='dense'
                            fullWidth
                            label="Sector"
                            placeholder="Sector"
                            value={sector}
                            onChange={handleSectorChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} xl={3}>
                        <ResponsiveSelect
                            options={[defaultDropdownOption].concat(subjects.data?.map((s) => ({
                                value: s.id,
                                label: s.name,
                            })))}
                            optionLabelFunction={(option) => option.label}
                            optionValueKey={'value'}
                            margin='dense'
                            fullWidth
                            label="Subject"
                            placeholder="Subject"
                            value={subject}
                            onChange={handleSubjectChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} xl={3}>
                        <ResponsiveSelect
                            options={[defaultDropdownOption].concat(frequencyDropdownValues)}
                            optionLabelFunction={(option) => option.label}
                            optionValueKey={'value'}
                            margin='dense'
                            fullWidth
                            label="Frequency"
                            placeholder="Frequency"
                            value={frequency}
                            onChange={handleFrequencyChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} xl={3}>
                        <TextField
                            className={classes.searchTextField}
                            label="Search Template Name"
                            variant="outlined"
                            size="small"
                            value={filterTemplateNameText || ""}
                            onChange={handleNameSearchChange}
                            helperText='Please enter at least4 character to search on'
                            error={showFilteredTextError()}
                            slotProps={{
                                formHelperText: { hidden: !showFilteredTextError() },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {filterTemplateNameText && (
                                                <Tooltip title="Clear" placement="bottom-end" classes={{ tooltip: classes.tooltip }}>
                                                    <IconButton className={classes.iconBtn}>
                                                        <ClearIcon className={`clearIcon ${classes.clearIcon}`} onClick={handleNameFilteredTextClear} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </InputAdornment>
                                    ),
                                    className: classes.endAdornment,
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} xl={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showArchived}
                                    onChange={handleShowArchivedChange}
                                    color="primary"
                                />
                            }
                            label="Show Archived"
                        />
                    </Grid>
                </Grid>
                <TableContainer>
                    <Table aria-label="Compliance">
                        <EnhancedTableHead
                            headCells={headCells}
                            sortColumn={complianceAdminTemplatesTable.sortBy}
                            sortDirection={complianceAdminTemplatesTable.orderBy || sortOrderName.DESC}
                            onRequestSort={handleSortClick}
                        />
                        <TableBody>{tableContent()}</TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={templates.data ? templates.data.totalCount : 0}
                    rowsPerPage={complianceAdminTemplatesTable.rowsPerPage}
                    page={complianceAdminTemplatesTable.page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
                <ArchiveTemplateDialog
                    open={!!archiveTemplate}
                    onCancel={() => setArchiveTemplate(null)}
                    archiveTemplate={archiveTemplate}
                    applicationArea={moduleAreas.COMPLIANCE}
                    page={complianceAdminTemplatesTable.page}
                    rows={complianceAdminTemplatesTable.rowsPerPage}
                    accountId={currentAccountId}
                    siteExternalIds={siteExternalIds}
                />
                
            </CardBase>
        </>
    );
}

export default ComplianceAdminTemplatesTableCard;
